import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { ActionsSubject, Store } from '@ngrx/store';
import { ofType } from '@ngrx/effects';
import { ToastrService } from 'ngx-toastr';
import * as MainActions from '../../store/main/main.actions';
import { InputLimit } from '../../shared/model/enum/input-limit';
import { MenuItemInterface, MainStateInterface, InterfaceCustomMailGroupInterface } from '../../store/main/main.model';
import { LayoutConstants } from '../layout-constants';
import {
  hoverOutsideSidebar,
  closeNavigationBar,
  fireClick,
  goLink,
  notificationOutsideClick,
  onResize,
  scroll,
  setBackgroundPattern,
  setHeaderAttributes,
  setMenuAttributes,
  setSidebarStates,
  toggleChat,
  toggleHeaderNavRight,
  toggleInnerChat,
  toggleLiveNotification,
  toggleOpened,
  toggleProfileNotification,
  toggleSidebarInDesktop,
} from '../../shared/helper/navbar-helper';
import { LayoutAnimations } from '../animations';
import { PageHeaderService } from '../../shared/service/page-header/page-header.service';
import { PageHeaderInterface } from '../../shared/service/page-header/page-header.model';
import { User } from '../../store/user/model';
import { MonitoringService } from '../../shared/service/error-service/monitoring.service';
import { DropdownSettings } from 'angular2-multiselect-dropdown/lib/multiselect.interface';
import { IotDeviceManagementPortalAppState } from '../../store/iotDeviceManagementPortalApp.reducer';
import { SearchOptionInterface } from './main.model';
import {takeWhile} from "rxjs/operators";

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: [
    './main.component.scss',
    '../../../assets/icon/icofont/css/icofont.scss',
    '../../../scss/navigation-bar.scss',
  ],
  animations: [
    LayoutAnimations.notificationBottom,
    LayoutAnimations.slideInOut,
    LayoutAnimations.mobileHeaderNavRight,
    LayoutAnimations.fadeInOutTranslate,
    LayoutAnimations.mobileMenuTop,
  ],
})
export class MainComponent extends LayoutConstants implements OnInit, OnDestroy {
  toggleSidebarInDesktop = toggleSidebarInDesktop;
  setSidebarStates = setSidebarStates;
  hoverOutsideSidebar = hoverOutsideSidebar;
  onResize = onResize;
  setHeaderAttributes = setHeaderAttributes;
  setMenuAttributes = setMenuAttributes;
  closeNavigationBar = closeNavigationBar;
  toggleHeaderNavRight = toggleHeaderNavRight;
  toggleProfileNotification = toggleProfileNotification;
  toggleOpened = toggleOpened;
  notificationOutsideClick = notificationOutsideClick;
  setBackgroundPattern = setBackgroundPattern;
  scroll = scroll;
  public newMenuItems: MenuItemInterface[];


  public pageHeader: PageHeaderInterface = {
    title: null,
    titleKey: null,
    icon: null,
    fullScreenButton: false,
    fullScreenTargetElementId: null,
    hideHeader: false,
  };

  readonly deviceType = {
    desktop: 'desktop',
    tablet: 'tablet',
    phone: 'phone',
  };


  private mainStoreSubscription: Subscription;
  private sendContactSupportCompletedActionSubscription: Subscription;
  private userStoreSubscription: Subscription;
  private lineStoreSubscription: Subscription;
  private pageHeaderSubscription: Subscription;
  private getOfflineSensorStatusCompletedSubscription: Subscription;
  private readonly storeSubscriptions: Subscription[] = [];

  public clock: Date;
  public toastPosition: string = 'toast-bottom-right';
  public customMailGroups: InterfaceCustomMailGroupInterface[];
  public customMailGroupsLoading: boolean;
  public userId: number;
  public isTokenLoaded: boolean = false;

  constructor(
    private http: HttpClient,
    private store: Store<IotDeviceManagementPortalAppState>,
    public router: Router,
    private toast: ToastrService,
    private mainActions: ActionsSubject,
    private pageHeaderService: PageHeaderService,
  ) {
    super();
    this.setHeaderAttributes(this.windowWidth);
    this.setMenuAttributes(this.windowWidth);
    this.setHeaderAttributes(this.windowWidth);

    this.isCallSupportSubmitButtonClicked = false;
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;

    this.router.events.subscribe((routerEvent) => {
      if (event instanceof NavigationStart) {
        this.closeNavigationBar();
      }
      if (routerEvent instanceof NavigationEnd) {
        // trick the Router into believing it's last link wasn't previously loaded
        this.router.navigated = false;
        window.scrollTo(0, 0);
      }
    });
  }


  reloadCurrentPage() {
    window.location.reload();
  }

  ngOnInit(): void {
    this.setBackgroundPattern('theme1');
    this.closeNavigationBar();

    this.store
      .select('user')
      .pipe(takeWhile(() => !this.isTokenLoaded))
      .subscribe((state: User) => {
          this.isTokenLoaded = state.isTokenRefreshed;
      });


    this.sendContactSupportCompletedActionSubscription = this.mainActions
      .pipe(ofType(MainActions.MainActionTypes.SendContactSupportCompleted))
      .subscribe((response: any) => {
        if (response.payload.success) {
          this.displayToastMessage('success', 'contactSuccessful');
        }

        if (response.payload.success === false && response.payload.data.userCount === 0) {
          this.displayToastMessage('error', 'contactNoContact');
        }

        if (!response.payload.success && response.payload.data === undefined) {
          this.displayToastMessage('error', 'contactWarning');
        }

      });

    if (this.pcodedDeviceType !== this.deviceType.desktop) {
      this.pageHeaderPaddingTop = '0';
    }

    this.pageHeaderSubscription = this.pageHeaderService.pageHeader.subscribe((item: PageHeaderInterface) => {
      this.pageHeader = {
        title: item.title,
        titleKey: item.titleKey,
        icon: item.icon,
        fullScreenButton: item.fullScreenButton,
        fullScreenTargetElementId: item.fullScreenTargetElementId,
        breadcrumbs: item.breadcrumbs,
        isTabPage: item.isTabPage,
        hideHeader: item.hideHeader,
      };
    });


  }

  displayToastMessage(type, messageKey) {
    const toastMessage = `general.${messageKey}`;
    const toastTitle = `general.${messageKey}`;

    if (type === 'success') {
      this.toast.success(toastMessage, toastTitle, {
        closeButton: true,
        progressBar: true,
        positionClass: this.toastPosition,
      });
    }
    if (type === 'error') {
      this.toast.error(toastMessage, toastTitle, {
        closeButton: true,
        progressBar: true,
        positionClass: this.toastPosition,
      });
    }
  }


  ngOnDestroy(): void {
    if (this.sendContactSupportCompletedActionSubscription) {
      this.sendContactSupportCompletedActionSubscription.unsubscribe();
    }
    if (this.userStoreSubscription) {
      this.userStoreSubscription.unsubscribe();
    }
    if (this.lineStoreSubscription) {
      this.lineStoreSubscription.unsubscribe();
    }

    if (this.pageHeaderSubscription) {
      this.pageHeaderSubscription.unsubscribe();
    }

    if (this.getOfflineSensorStatusCompletedSubscription) {
      this.getOfflineSensorStatusCompletedSubscription.unsubscribe();
    }

    if (this.mainStoreSubscription) {
      this.mainStoreSubscription.unsubscribe();
    }

  }

  ngAfterContentInit(): void {
    // const oneSecondTimer = timer(0, 1000);
    // this.timer = oneSecondTimer.subscribe(() => {
    //   // this.clock = getCurrentDateTime(this.timeZone$);
    // });
  }
}
